/**
 * network/PostComposer — post-in-a-card composer for the network-style feed.
 * Supports:
 *   - Plain text (up to 5000 chars).
 *   - Auto-detects a bare URL and offers a link-preview via
 *     POST /api/feed/link-preview.
 *   - Attaching a single image via the existing object-storage endpoint
 *     (POST /api/uploads/forum) which returns { url }.
 *
 * Props:
 *   onCreated(post) — called with the hydrated post so the parent can prepend.
 */
import React, { useContext, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Image as ImageIcon, Link2, Send, Loader2, X, Sparkles,
} from 'lucide-react';

const URL_RE = /(https?:\/\/[^\s]+)/i;
const MAX_LEN = 5000;

const PostComposer = ({ onCreated }) => {
  const { API, user } = useContext(AuthContext);
  const fileRef = useRef(null);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);        // link preview payload
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewDismissed, setPreviewDismissed] = useState(false);
  const [image, setImage] = useState(null);            // uploaded image url

  const detectedUrl = body.match(URL_RE)?.[1];

  React.useEffect(() => {
    // Fetch link preview when a URL appears (debounced).
    if (!detectedUrl || previewDismissed) { setPreview(null); return; }
    if (preview?.url === detectedUrl) return;
    const h = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const { data } = await axios.post(`${API}/feed/link-preview`, { url: detectedUrl });
        setPreview(data);
      } catch { /* silent — bare url still posts fine */ }
      finally { setPreviewLoading(false); }
    }, 600);
    return () => clearTimeout(h);
  }, [detectedUrl, previewDismissed, API, preview]);

  const pickImage = () => fileRef.current?.click();
  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Max 8 MB per image'); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(`${API}/uploads/forum`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = data?.url || data?.file_url || data?.file?.url;
      if (!url) throw new Error('Upload returned no URL');
      setImage(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); e.target.value = ''; }
  };

  const submit = async () => {
    if (!body.trim() && !image && !preview) { toast.error('Nothing to post yet'); return; }
    const media = [];
    if (image)   media.push({ type: 'image', url: image });
    if (preview) media.push({ ...preview, type: 'link' });
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/feed/posts`, {
        body: body.trim(),
        media,
        visibility: 'public',
      });
      onCreated && onCreated(data);
      setBody('');
      setImage(null);
      setPreview(null);
      setPreviewDismissed(false);
      toast.success('Posted');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not post'); }
    finally { setBusy(false); }
  };

  const chars = body.length;

  return (
    <div className="pro-card p-4" data-testid="post-composer">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-primary/15 text-primary flex items-center justify-center text-sm font-bold flex-shrink-0 overflow-hidden">
          {user?.avatar_url
            ? <img src={user.avatar_url} alt="" className="h-full w-full object-cover" />
            : (user?.name || user?.email || '?').slice(0, 2).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <Textarea
            rows={3}
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, MAX_LEN))}
            placeholder={`What's happening in your freeze-dry world, ${(user?.name || user?.email || '').split(' ')[0] || 'there'}?`}
            data-testid="composer-body"
            className="text-sm border-0 focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none px-2 py-2 resize-none"
          />

          {previewLoading && (
            <div className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-1">
              <Loader2 className="w-3 h-3 animate-spin" /> fetching preview…
            </div>
          )}

          {preview && !previewDismissed && (
            <div className="mt-2 relative rounded-lg border border-border overflow-hidden" data-testid="composer-link-preview">
              <button
                type="button"
                onClick={() => { setPreview(null); setPreviewDismissed(true); }}
                className="absolute top-1.5 right-1.5 z-10 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
              >
                <X className="w-3 h-3" />
              </button>
              <div className="flex items-stretch gap-3 bg-muted/30">
                {preview.thumbnail && <img src={preview.thumbnail} alt="" className="w-28 h-24 object-cover flex-shrink-0" />}
                <div className="min-w-0 flex-1 py-2 pr-8">
                  {preview.site_name && <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground truncate">{preview.site_name}</div>}
                  {preview.title && <div className="font-heading font-extrabold text-sm truncate">{preview.title}</div>}
                  {preview.description && <div className="text-xs text-muted-foreground line-clamp-2">{preview.description}</div>}
                </div>
              </div>
            </div>
          )}

          {image && (
            <div className="mt-2 relative rounded-lg overflow-hidden border border-border" data-testid="composer-image-preview">
              <button
                type="button"
                onClick={() => setImage(null)}
                className="absolute top-1.5 right-1.5 z-10 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
              >
                <X className="w-3 h-3" />
              </button>
              <img src={image} alt="" className="w-full max-h-72 object-cover" />
            </div>
          )}

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={uploadImage}
            className="hidden"
            data-testid="composer-file-input"
          />

          <div className="mt-2 flex items-center justify-between">
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={pickImage}
                disabled={uploading}
                className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold text-muted-foreground hover:text-primary hover:bg-primary/5 transition-colors"
                data-testid="composer-image-btn"
              >
                {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />} Image
              </button>
              {detectedUrl && !preview && !previewDismissed && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-primary bg-primary/10 rounded-full px-2 py-0.5">
                  <Link2 className="w-3 h-3" /> URL detected
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-[11px] font-mono ${chars > MAX_LEN * 0.9 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                {chars}/{MAX_LEN}
              </span>
              <Button
                size="sm"
                onClick={submit}
                disabled={busy || (!body.trim() && !image && !preview)}
                className="rounded-full h-8 font-bold"
                data-testid="composer-submit"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Sparkles className="w-3.5 h-3.5 mr-1" /> Post</>}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostComposer;
